import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./announcement.css";
import { ANNOUNCEMENT, Announcement } from "./announcement";

// Same private routes the TECHNOLIVE pop-up stays off.
const excluded = /^\/(admin|checkout|payment|orders|account|login|register|forgot-password|reset-password|cart)(\/|$)/;
const dismissalKey = (a: Announcement) => `announcement-${a.id}-dismissed`;

function wasDismissed(a: Announcement) {
  try { return sessionStorage.getItem(dismissalKey(a)) === "yes"; } catch { return false; }
}

export function isLive(a: Pick<Announcement, "startsAt" | "endsAt">, now = Date.now()) {
  return now >= Date.parse(a.startsAt) && now < Date.parse(a.endsAt);
}

// Slim site-wide bar above the header. A display banner only, so the device clock is good enough for its dates.
export default function AnnouncementBar({ announcement = ANNOUNCEMENT, now }: { announcement?: Announcement | null; now?: number }) {
  const { pathname } = useLocation();
  const [closedId, setClosedId] = useState<string | null>(null);
  const [clock, setClock] = useState(Date.now);
  useEffect(() => {
    if (!announcement || now !== undefined) return;
    const next = [announcement.startsAt, announcement.endsAt].map(Date.parse).find(time => time > clock);
    if (!next) return;
    const timer = window.setTimeout(() => setClock(Date.now()), Math.min(next - clock, 2147483647));
    return () => window.clearTimeout(timer);
  }, [announcement, clock, now]);
  const closed = announcement && (closedId === announcement.id || wasDismissed(announcement));
  if (!announcement || closed || excluded.test(pathname) || !isLive(announcement, now ?? clock)) return null;
  const close = () => {
    try { sessionStorage.setItem(dismissalKey(announcement), "yes"); } catch {}
    setClosedId(announcement.id);
  };
  return (
    <aside className="ab-bar" aria-label="Announcement">
      <p>
        <span className="ab-label">{announcement.label}</span>
        <span>{announcement.text}</span>{" "}
        <Link to={announcement.to}>{announcement.linkLabel}</Link>
      </p>
      <button type="button" className="ab-close" aria-label="Close announcement" onClick={close}>×</button>
    </aside>
  );
}
